import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { BufferedTextInput } from 'record-it-components/input/TextInput/TextInput';
import {
  SingleTabEditDialog,
  SingleTabEditDialogProps
} from 'record-it-components/modal/SingleTabEditDialog/SingleTabEditDialog';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useEntityPropertyChangedCallback } from '../../../hooks/entity/useEntityPropertyChangedCallback';
import { useModifyEntityCallback } from '../../../hooks/entity/useModifyEntityCallback';
import {
  ChangePassword,
  ExpandableChangePasswordForm
} from 'record-it-components/form/ExpandableChangePasswordForm/ExpandableChangePasswordForm';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { ApiAdapter } from '../../../classes/ApiAdapter/ApiAdapter';

export type UserProfileDialogProps = PropsWithoutChildren<
  Omit<SingleTabEditDialogProps, 'tabTitle'>
>;

export const UserProfileDialog: FunctionComponent<UserProfileDialogProps> =
  React.memo((props) => {
    const { ...remainingProps } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const { getRequiredCurrentUser } = useCurrentUser();
    const currentUser = getRequiredCurrentUser();

    const modifyUser = useModifyEntityCallback(EntityName.User);
    const handleUserNameChanged = useEntityPropertyChangedCallback(
      currentUser,
      'name',
      modifyUser
    );

    const changePassword = useEventCallback<ChangePassword>(
      async ({ currentPassword, newPassword }) => {
        const response = await ApiAdapter.changePassword({
          currentPassword,
          newPassword
        });

        if (!response.success) {
          throw new Error('got unsuccessful response');
        }
      }
    );

    return (
      <SingleTabEditDialog
        {...remainingProps}
        tabTitle={t('components.user.UserProfileDialog.title')}
      >
        <FormGroup>
          <BufferedTextInput
            label={t('entities.User.name')}
            value={currentUser.name}
            onInput={handleUserNameChanged}
            data-testid="UserProfileDialog.NameInput"
          ></BufferedTextInput>
        </FormGroup>
        <FormGroup>
          <ExpandableChangePasswordForm
            changePassword={changePassword}
            data-testid="UserProfileDialog.ExpandableChangePasswordForm"
          ></ExpandableChangePasswordForm>
        </FormGroup>
      </SingleTabEditDialog>
    );
  });
